import { A0, SEAT, SOPS, STEPS, type Sop, type SopNode } from './sop.data';

// Logic thuần của Mint-SOP: toạ độ lưu đồ, ma trận RACI, bài hướng dẫn. Số đo chép từ board (H_LW 132 · H_CW 128 · H_LH 88 · nút 112×60).

export const seatK = (c: string): string => SEAT[c] ?? c;
export const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const fmtIso = (s: string): string => (s ? `${s.slice(8, 10)}/${MON[+s.slice(5, 7) - 1]}/${s.slice(0, 4)}` : '');
export const norm = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').toLowerCase();

/** Màu loại dịch vụ / bước app — chỉ token màu của hệ thống. */
export const SVC: Record<string, string> = { frt: 'var(--d-blue)', trf: 'var(--ok)', ccf: 'var(--d-violet)', coo: 'var(--ok-dot)', ddl: 'var(--d-amber)', fum: 'var(--d-green)', isr: 'var(--d-blue)', ptf: 'var(--ok)', scy: 'var(--d-gray)' };
const STEP_C = ['var(--d-blue)', 'var(--d-violet)', 'var(--ok-dot)', 'var(--d-amber)', 'var(--d-green)', 'var(--route)', 'var(--link)', 'var(--err)'];

export { SOPS };
export type { Sop, SopNode };

export function stepsOf(s: Sop): string[] {
  const out: string[] = [];
  s.n.forEach((n) => { if (n[2] && !out.includes(n[2])) out.push(n[2]); });
  return out;
}
export const stepColor = (s: Sop, step: string): string => (step ? STEP_C[stepsOf(s).indexOf(step) % STEP_C.length]! : 'var(--d-gray)');

const H_LW = 132, H_CW = 128, H_LH = 88, H_NW = 112, H_NH = 60, V_RH = 74, V_TOP = 36;
export interface Geo { fw: number; fh: number; lw: number; pos: (n: SopNode) => { cx: number; cy: number; w: number; h: number } }
export function geometry(s: Sop, horizontal: boolean, availW: number): Geo {
  const maxCol = Math.max(...s.n.map((n) => n[4]));
  const vlw = Math.max(108, Math.floor(availW / s.l.length));
  const li = (l: string) => Math.max(0, s.l.indexOf(l));
  const pos = (n: SopNode) => (horizontal
    ? { cx: H_LW + (n[4] - 1) * H_CW + H_CW / 2, cy: li(n[3]) * H_LH + H_LH / 2, w: H_NW, h: H_NH }
    : { cx: li(n[3]) * vlw + vlw / 2, cy: V_TOP + (n[4] - 1) * V_RH + V_RH / 2, w: vlw - 16, h: 52 });
  return { fw: horizontal ? H_LW + maxCol * H_CW + 12 : s.l.length * vlw, fh: horizontal ? s.l.length * H_LH : V_TOP + maxCol * V_RH + 12, lw: horizontal ? H_LW : vlw, pos };
}

export interface Edge { d: string; tone: string; label: string; lx: number; ly: number; dash: boolean }
export function edges(s: Sop, g: Geo, horizontal: boolean): Edge[] {
  const by: Record<string, SopNode> = {};
  s.n.forEach((n) => { by[n[0]] = n; });
  return s.g.filter(([a, b]) => by[a] && by[b]).map(([a, b, label, tone]) => {
    const A = g.pos(by[a]!), B = g.pos(by[b]!);
    let d: string;
    if (horizontal) {
      if (B.cx > A.cx) { const sx = A.cx + A.w / 2, ex = B.cx - B.w / 2 - 2, mx = Math.round(sx + Math.min(16, (ex - sx) / 2)); d = `M${sx} ${A.cy} H${mx} V${B.cy} H${ex}`; }
      else { const top = Math.min(A.cy, B.cy) - A.h / 2 - 12; d = `M${A.cx} ${A.cy - A.h / 2} V${top} H${B.cx} V${B.cy - B.h / 2 - 2}`; }
    } else if (B.cy > A.cy) { const sy = A.cy + A.h / 2, ey = B.cy - B.h / 2 - 2, my = Math.round(sy + Math.min(12, (ey - sy) / 2)); d = `M${A.cx} ${sy} V${my} H${B.cx} V${ey}`; }
    else { const rx = Math.max(A.cx, B.cx) + A.w / 2 + 10; d = `M${A.cx + A.w / 2} ${A.cy} H${rx} V${B.cy} H${B.cx + B.w / 2 + 2}`; }
    const ly = horizontal ? (B.cx > A.cx ? Math.min(A.cy, B.cy) - 6 : Math.min(A.cy, B.cy) - A.h / 2 - 16) : (A.cy + B.cy) / 2;
    return { d, tone, label, lx: Math.round((A.cx + B.cx) / 2), ly: Math.round(ly), dash: tone === 'slate' };
  });
}

export function raciSeats(s: Sop): string[] {
  const out: string[] = [];
  s.n.forEach((n) => Object.values(n[6] ?? {}).forEach((arr) => arr.forEach((x) => { if (!x.startsWith('EXT') && !out.includes(x)) out.push(x); })));
  s.l.forEach((l) => { if (l.startsWith('EXT') && s.n.some((n) => Object.values(n[6] ?? {}).some((a) => a.includes(l))) && !out.includes(l)) out.push(l); });
  return out;
}
export const raciCell = (n: SopNode, seat: string): string => (['R', 'A', 'C', 'I'] as const).filter((r) => (n[6]?.[r] ?? []).includes(seat)).join('');

export function nodeRows(n: SopNode): { k: string; v: string }[] {
  const det = n[7];
  if (!det) return [];
  return [['Mô tả', det[0]], ['Đầu vào', det[1]], ['Đầu ra', det[2]], ['Luật', det[3]], ['SLA', det[4]], ['Bằng chứng', det[5]]].filter(([, v]) => v).map(([k, v]) => ({ k: k!, v: v! })).slice(1);
}

export function sopAnswer(s: Sop, q: string): string {
  const x = norm(q);
  if (/kpi|dung han|chi tieu/.test(x) && s.kp[0]) return `KPI chính: ${s.kp.slice(0, 2).map((k) => `${k[0]} (${k[2]})`).join('; ')}.`;
  const hit = s.n.find((n) => n[7] && norm(`${n[7][0]} ${n[7][3]} ${n[7][4]}`).split(' ').some((w) => w.length > 3 && x.includes(w))) ?? s.n.find((n) => norm(n[5]).split(' ').some((w) => w.length > 3 && x.includes(w)));
  if (hit) return `${hit[0]} · ${hit[5]}: ${hit[7]?.[0] ?? seatK(hit[3])}${hit[7]?.[4] ? ` — SLA: ${hit[7][4]}` : ''}`;
  return `Em chưa thấy ý này trong ${s.c}. Thử hỏi về một bước, SLA hoặc KPI.`;
}

// ----- Hướng dẫn sử dụng -----
export const MODS: [string, string][] = [['job-report', 'Job Report'], ['request-list', 'Request List'], ['freight-data', 'Freight Data'], ['my-inbox', 'My Inbox']];
export const modK = (k: string): string => MODS.find((m) => m[0] === k)?.[1] ?? k;
const UPD = ['20/Oct/2026', '14/Oct/2026', '02/Oct/2026', '25/Sep/2026'];
export interface Article { id: string; mod: string; slug: string; t: string; cat: string; sum: string; kw: string[]; vid: boolean; ver: string; upd: string; steps: string[] }
export const ARTICLES: Article[] = A0.map((a, i) => ({
  id: `a${i}`, mod: a[0], slug: a[1], t: a[2], cat: a[3], sum: a[4], kw: a[5].split(',').map((x) => x.trim()), vid: !!a[6], ver: `1.${i % 4}`, upd: UPD[i % 4]!,
  steps: STEPS[`${a[0]}/${a[1]}`] ?? [`Mở ${modK(a[0])} từ menu bên trái.`, `Dùng chức năng “${a[2]}” trên thanh công cụ của màn.`, 'Kết quả cập nhật ngay; bấm ? ở góc phải màn để mở lại bài này.'],
}));
export const filterArticles = (q: string): Article[] => { const n = norm(q.trim()); return n ? ARTICLES.filter((a) => norm(`${a.t} ${a.sum} ${a.kw.join(' ')} ${a.cat} ${modK(a.mod)}`).includes(n)) : ARTICLES; };
export const filterSops = (q: string): Sop[] => { const n = norm(q.trim()); return n ? SOPS.filter((s) => norm(`${s.c} ${s.t} ${s.n.map((x) => x[5]).join(' ')}`).includes(n)) : SOPS; };
